import { useMemo, useState } from "react";
import { ListingHeader } from "./listing/ListingHeader";
import { ListingFooter } from "./listing/ListingFooter";
import { MobileContactBar } from "./listing/MobileContactBar";
import { SponsorCard } from "./listing/SponsorCard";
import { SponsorBanner } from "./listing/SponsorBanner";
import { PremierAgentCard } from "./listing/PremierAgentCard";
import { AdvertiserInquiryDialog } from "./listing/AdvertiserInquiryDialog";
import { AdvertiserSignupDialog } from "./listing/AdvertiserSignupDialog";
import { ShowingRequestDialog } from "./listing/ShowingRequestDialog";
import { LocalExpertsButton } from "./listing/LocalExpertsButton";
import { useListingData } from "./listing/useListingData";
import { useHashScroll } from "./listing/useHashScroll";
import { CATEGORY_LABELS } from "./listing/constants";

const SPONSORS_HERO_IMAGE = "https://images.unsplash.com/photo-1632323187625-7a4e4414338e?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function PropertyWebsiteSponsors() {
  const { slug, preview, listing, error } = useListingData();
  const [inquireAd, setInquireAd] = useState(null);
  const [signupOpen, setSignupOpen] = useState(false);
  const [showingOpen, setShowingOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { featured, byCategory } = useMemo(() => {
    if (!listing) return { featured: null, byCategory: {} };
    const sponsors = listing.sponsors || {};
    const byId = new Map();
    Object.values(sponsors).forEach((list) => (list || []).forEach((ad) => byId.set(ad.id, ad)));
    const top = (sponsors.top_banner || sponsors.mid_banner || sponsors.lower_banner || [])[0] || null;
    const groups = {};
    for (const ad of byId.values()) {
      if (ad.id === top?.id) continue;
      (groups[ad.category] ||= []).push(ad);
    }
    return { featured: top, byCategory: groups };
  }, [listing]);

  useHashScroll(Boolean(listing));

  if (error) return <main data-testid="public-listing-error" className="min-h-screen bg-white grid place-items-center p-8 text-center"><h1 className="text-xl font-serif-display text-[#202833]">{error}</h1></main>;
  if (!listing) return <main data-testid="public-listing-loading" className="min-h-screen grid place-items-center bg-white font-mono text-sm text-[#667085]">Loading sponsors…</main>;

  const contact = listing.contact;
  const brandLabel = contact?.name || listing.full_address || listing.project_name;
  const showSponsors = listing.sponsors_enabled !== false;

  return (
    <main data-testid="public-property-sponsors" className="min-h-screen bg-white text-[#202833]">
      {preview && <div data-testid="public-listing-preview-banner" className="bg-[#C7A361] text-[#111820] text-center text-xs font-mono uppercase tracking-[0.14em] py-2">Admin preview — this is how the page will look to visitors</div>}
      <ListingHeader slug={slug} brandLabel={brandLabel} preview={preview} onRequestShowing={() => setShowingOpen(true)} onMenuOpenChange={setMobileMenuOpen} />

      <section className="relative bg-[#111820] text-white py-16 sm:py-24 overflow-hidden">
        <img src={SPONSORS_HERO_IMAGE} alt="" className="absolute inset-y-0 right-0 w-1/2 h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#111820] via-[#111820]/90 to-transparent" />
        <div className="relative mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-10">
          <span className="text-[11px] font-mono uppercase tracking-[.16em] text-[#C7A361]">Our Trusted Partners</span>
          <h1 data-testid="public-sponsors-title" className="font-serif-display text-3xl sm:text-4xl mt-2">Featured Sponsors</h1>
          <p className="mt-3 text-sm sm:text-base text-white/60 max-w-xl">Businesses recommended by the listing agent to help with your next move near {listing.full_address || listing.project_name}.</p>
        </div>
      </section>

      <div className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-10 py-12 sm:py-16 space-y-14">
        {showSponsors && featured && <SponsorBanner advertiser={featured} onInquire={setInquireAd} />}

        {showSponsors && Object.keys(byCategory).length > 0 ? (
          Object.keys(CATEGORY_LABELS).filter((cat) => byCategory[cat]?.length).map((cat) => (
            <div key={cat} id={`sponsors-${cat}`} className="scroll-mt-24">
              <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5">{cat === "real_estate_agent" ? "Premier Real Estate Agents" : CATEGORY_LABELS[cat]}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {byCategory[cat].map((ad) => (
                  cat === "real_estate_agent" ? <PremierAgentCard key={ad.id} advertiser={ad} /> : <SponsorCard key={ad.id} advertiser={ad} onInquire={setInquireAd} />
                ))}
              </div>
            </div>
          ))
        ) : (
          !featured && <p data-testid="public-sponsors-empty" className="text-center text-sm text-[#667085] py-10">No local service partners have been added for this property yet.</p>
        )}
      </div>

      <section id="advertise" className="scroll-mt-24 bg-[#111820] text-white py-14 sm:py-20 border-t border-white/10">
        <div className="mx-auto max-w-xl px-4 sm:px-6 text-center">
          <h2 className="font-serif-display text-xl sm:text-2xl">Become a Featured Sponsor</h2>
          <p className="mt-3 text-sm text-white/60">Showcase your business to potential homebuyers and sellers viewing this listing.</p>
          <button data-testid="public-sponsors-advertise-cta" onClick={() => setSignupOpen(true)} className="mt-6 rounded-full bg-[#C7A361] text-[#111820] text-xs font-semibold uppercase tracking-[0.08em] px-6 py-3 hover:brightness-95 transition">Get Sponsor Information</button>
        </div>
      </section>

      <ListingFooter slug={slug} brandLabel={brandLabel} brokerage={contact?.company} preview={preview} />
      <MobileContactBar phone={contact?.phone} onRequestShowing={() => setShowingOpen(true)} hidden={showingOpen || signupOpen || Boolean(inquireAd) || mobileMenuOpen} />
      <LocalExpertsButton slug={slug} sponsorsByCategory={showSponsors ? byCategory : {}} onSponsorsPage />

      <ShowingRequestDialog open={showingOpen} onClose={() => setShowingOpen(false)} slug={slug} agentName={contact?.name} address={listing.full_address || listing.project_name} />
      <AdvertiserInquiryDialog advertiser={inquireAd} slug={slug} onClose={() => setInquireAd(null)} />
      <AdvertiserSignupDialog open={signupOpen} onClose={() => setSignupOpen(false)} slug={slug} />
    </main>
  );
}
